<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h4 class="mb-0">{{ $t("saisiePaiement.title") }}</h4>
      <router-link to="/decaissement/nouvelle-depense">
        <a-button type="primary" icon="plus" class="action-button">
          {{ $t("factures.createNewDepense") }}
        </a-button>
      </router-link>
    </div>
    <div class="card-body">
      <!-- Filters -->
      <div class="filters-section decaissement-filters-stacked">
        <div class="filters-header decaissement-filters-header">
          <h4>
            <a-icon type="filter" class="filter-icon" />
            {{ $t("all.filtresEtRecherche") }}
          </h4>
          <div class="action-button-group">
            <a-button class="action-button" @click="resetFilters">
              {{ $t("factures.resetFilters") }}
            </a-button>
            <a-button
              type="primary"
              class="action-button"
              @click="applyFilters"
            >
              {{ $t("action.search") }}
            </a-button>
          </div>
        </div>
        <a-row :gutter="[16, 16]" type="flex" class="decaissement-filters-row">
          <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="usergroup-add" class="action-icon" />
                {{ $t("saisiePaiement.filterBeneficiaryType") }}
              </label>
              <a-select
                v-model="filters.beneficiaryType"
                allow-clear
                class="filter-control ben-type-filter-select"
                :placeholder="$t('all.tpiAll')"
                @change="applyFilters"
              >
                <a-select-option
                  value="provider"
                  :label="$t('factures.beneficiaryProvider')"
                >
                  <beneficiary-type-display type="provider" />
                </a-select-option>
                <a-select-option
                  value="associate"
                  :label="$t('factures.beneficiaryAssociate')"
                >
                  <beneficiary-type-display type="associate" />
                </a-select-option>
                <a-select-option
                  value="employee"
                  :label="$t('factures.beneficiaryEmployee')"
                >
                  <beneficiary-type-display type="employee" />
                </a-select-option>
                <a-select-option
                  value="company"
                  :label="$t('factures.beneficiaryCompany')"
                >
                  <beneficiary-type-display type="company" />
                </a-select-option>
                <a-select-option
                  value="debiteur"
                  :label="$t('factures.beneficiaryDebiteur')"
                >
                  <beneficiary-type-display type="debiteur" />
                </a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="tags" class="action-icon" />
                {{ $t("saisiePaiement.filterExpenseMode") }}
              </label>
              <a-select
                v-model="filters.expenseMode"
                allow-clear
                class="filter-control"
                :placeholder="$t('all.tpiAll')"
                @change="applyFilters"
              >
                <a-select-option value="normal">{{
                  $t("decaissement.normal")
                }}</a-select-option>
                <a-select-option value="declaree">{{
                  $t("decaissement.declaree")
                }}</a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="calendar" class="action-icon" />
                {{ $t("saisiePaiement.filterDueDateRange") }}
              </label>
              <a-range-picker
                v-model="filters.dateRange"
                class="filter-control date-range-filter"
                format="DD/MM/YYYY"
                style="width: 100%"
                @change="applyFilters"
              />
            </div>
          </a-col>
          <a-col :xs="24" :sm="24" :md="24" :lg="24" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="search" class="action-icon" />
                {{ $t("saisiePaiement.tableSearch") }}
              </label>
              <a-input-search
                v-model="filters.search"
                class="filter-control"
                :placeholder="$t('saisiePaiement.tableSearchPlaceholder')"
                allow-clear
                @search="applyFilters"
              />
            </div>
          </a-col>
        </a-row>
      </div>

      <beneficiary-types-legend />

      <!-- Pay bar -->
      <div v-if="selectedIds.length > 0" class="pay-bar mb-3">
        <span
          >{{ $t("saisiePaiement.totalSelected") }}:
          <span class="amount-cell">{{ selectedTotal.toFixed(3) }}</span></span
        >
        <a-button type="primary" class="action-button" @click="openPayModal">
          {{ $t("saisiePaiement.paySelected") }} ({{ selectedIds.length }})
        </a-button>
      </div>

      <!-- Table -->
      <div class="table-container">
        <a-table
          row-key="_id"
          :loading="loading"
          :columns="columns"
          :data-source="rows"
          :pagination="tablePagination"
          :scroll="{ x: 'max-content' }"
          :row-selection="{
            selectedRowKeys: selectedIds,
            onChange: onSelectChange,
          }"
          :custom-row="rowClickHandler"
          :row-class-name="rowClassName"
          @change="onTableChange"
        >
          <template slot="beneficiaryType" slot-scope="text">
            <span v-if="text === 'provider'" class="ben-type"
              ><a-icon type="shop" class="legend-provider" />
              {{ $t("factures.beneficiaryProvider") }}</span
            >
            <span v-else-if="text === 'associate'" class="ben-type"
              ><a-icon type="team" class="legend-associate" />
              {{ $t("factures.beneficiaryAssociate") }}</span
            >
            <span v-else-if="text === 'employee'" class="ben-type"
              ><a-icon type="user" class="legend-employee" />
              {{ $t("factures.beneficiaryEmployee") }}</span
            >
            <span v-else-if="text === 'company'" class="ben-type"
              ><a-icon type="apartment" class="legend-company" />
              {{ $t("factures.beneficiaryCompany") }}</span
            >
            <span v-else-if="text === 'debiteur'" class="ben-type"
              ><a-icon type="bank" class="legend-debiteur" />
              {{ $t("factures.beneficiaryDebiteur") }}</span
            >
            <span v-else class="ben-type">—</span>
          </template>
          <template slot="expenseLabel" slot-scope="text">
            <span class="client-name">{{ text || "—" }}</span>
          </template>
          <template slot="invoiceRef" slot-scope="text">
            <span class="invoice-cell">{{ text || "—" }}</span>
          </template>
          <template slot="beneficiary" slot-scope="text">
            <span class="client-name">{{ text || "—" }}</span>
          </template>
          <template slot="expenseMode" slot-scope="text">
            <a-tag v-if="text === 'normal'" color="blue">{{
              $t("decaissement.normal")
            }}</a-tag>
            <a-tag v-else-if="text === 'declaree'" color="orange">{{
              $t("decaissement.declaree")
            }}</a-tag>
            <span v-else>—</span>
          </template>
          <template slot="dueDate" slot-scope="text">
            <span class="date-cell">{{ formatDate(text) }}</span>
          </template>
          <template slot="amount" slot-scope="text">
            <span class="amount-cell">{{
              text != null ? text.toFixed(3) : "—"
            }}</span>
          </template>
          <template slot="action" slot-scope="text, record">
            <a-button
              type="link"
              size="small"
              @click.stop="editSchedule(record)"
            >
              <a-icon type="edit" />
            </a-button>
          </template>
        </a-table>
      </div>
      <div
        v-if="!loading && rows.length === 0"
        class="text-center text-muted py-4 empty-state"
      >
        {{ $t("saisiePaiement.noPendingSchedules") }}
      </div>
    </div>

    <!-- Pay modal -->
    <a-modal
      :visible="payModalVisible"
      :title="$t('saisiePaiement.confirmPayment')"
      :confirm-loading="paying"
      :width="payModalWidth"
      @ok="submitPayment"
      @cancel="payModalVisible = false"
      destroy-on-close
    >
      <div class="mb-3">
        <div class="text-muted mb-1">
          {{ $t("saisiePaiement.totalSelected") }}
        </div>
        <a-input-number
          :value="selectedTotal"
          disabled
          style="width: 100%"
          :precision="3"
        />
      </div>
      <p class="text-muted small mb-2">
        {{ $t("saisiePaiement.splitPaymentsHint") }}
      </p>

      <div
        v-for="(line, idx) in paymentLines"
        :key="line.uid"
        class="payment-split-card mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>{{
            $t("saisiePaiement.paymentLine", { n: idx + 1 })
          }}</strong>
          <a-button
            v-if="paymentLines.length > 1"
            type="link"
            size="small"
            class="text-danger p-0"
            @click="removePaymentLine(idx)"
          >
            {{ $t("saisiePaiement.removePaymentLine") }}
          </a-button>
        </div>
        <a-row :gutter="[12, 8]">
          <a-col :xs="24" :sm="12">
            <div class="mb-2">
              <label class="filter-label">{{
                $t("saisiePaiement.selectMethod")
              }}</label>
              <a-select
                v-model="line.method"
                style="width: 100%"
                @change="onLineMethodChange(idx)"
              >
                <a-select-option value="cash">{{
                  $t("decaissement.paymentMethod.cash")
                }}</a-select-option>
                <a-select-option value="cheque">{{
                  $t("decaissement.paymentMethod.cheque")
                }}</a-select-option>
                <a-select-option value="virement">{{
                  $t("decaissement.paymentMethod.virement")
                }}</a-select-option>
                <a-select-option value="traite">{{
                  $t("decaissement.paymentMethod.traite")
                }}</a-select-option>
                <a-select-option value="tpe">{{
                  $t("decaissement.paymentMethod.tpe")
                }}</a-select-option>
                <a-select-option value="prelevement">{{
                  $t("decaissement.paymentMethod.prelevement")
                }}</a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12">
            <div
              class="mb-2"
              :class="{
                'payment-field--insufficient': lineAmountExceedsAvailable(line),
              }"
            >
              <label class="filter-label">{{ $t("recette.montant") }}</label>
              <a-input-number
                v-model="line.amount"
                :min="0"
                :precision="3"
                style="width: 100%"
              />
            </div>
          </a-col>
        </a-row>
        <template v-if="line.method === 'cash'">
          <div class="mb-2">
            <label class="filter-label">{{
              $t("decaissement.fundSource")
            }}</label>
            <a-select
              v-model="line.fundDestination"
              style="width: 100%"
              @change="onLineFundDestinationChange(idx)"
            >
              <a-select-option value="myCaisse" :disabled="!myCaisseId">{{
                $t("decaissement.myCaisse")
              }}</a-select-option>
              <a-select-option
                value="otherCaisse"
                :disabled="!depenseOtherCashOptions.length"
                >{{ $t("decaissement.otherCaisse") }}</a-select-option
              >
            </a-select>
          </div>
          <div
            v-if="line.fundDestination === 'myCaisse'"
            class="mb-2"
            :class="{
              'payment-field--insufficient':
                line.caisseId && lineCashAmountExceedsCaisse(line),
            }"
          >
            <label class="filter-label">{{
              $t("decaissement.cashName")
            }}</label>
            <!-- Enabled as soon as the user owns more than one caisse. -->
            <a-select
              v-model="line.caisseId"
              style="width: 100%"
              :disabled="myCaisses.length <= 1"
              :placeholder="$t('decaissement.validation.selectCaisse')"
            >
              <a-select-option v-for="c in myCaisses" :key="c._id" :value="c._id"
                >{{ c.name }} —
                {{ cashAvailabilityForCaisse(c, line.method) }}</a-select-option
              >
            </a-select>
          </div>
          <div
            v-if="line.fundDestination === 'otherCaisse'"
            class="mb-2"
            :class="{
              'payment-field--insufficient':
                line.caisseId && lineCashAmountExceedsCaisse(line),
            }"
          >
            <label class="filter-label">{{
              $t("decaissement.cashName")
            }}</label>
            <a-select
              v-model="line.caisseId"
              style="width: 100%"
              :placeholder="$t('requis.required')"
            >
              <a-select-option
                v-for="c in depenseOtherCashOptions"
                :key="c._id"
                :value="c._id"
                >{{ c.name }} —
                {{ cashAvailabilityForCaisse(c, line.method) }}</a-select-option
              >
            </a-select>
          </div>
          <p v-if="line.caisseId" class="text-muted small mb-0 mt-1">
            {{ $t("decaissement.availableForThisPayment") }}:
            <strong>{{ cashAvailabilityFormatted(line) }}</strong>
          </p>
          <p
            v-if="line.caisseId && lineCashAmountExceedsCaisse(line)"
            class="small mb-0 mt-1 text-danger"
          >
            {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
          </p>
        </template>
        <div
          v-else
          class="mb-2"
          :class="{
            'payment-field--insufficient':
              line.caisseBankId && lineBankAmountExceedsCaisse(line),
          }"
        >
          <label class="filter-label">{{
            $t("decaissement.caisseBank")
          }}</label>
          <a-select
            v-model="line.caisseBankId"
            allow-clear
            style="width: 100%"
            :placeholder="$t('requis.required')"
          >
            <a-select-option
              v-for="b in lookups.caisseBanks"
              :key="b._id"
              :value="b._id"
              >{{ b.name }} —
              {{ bankAvailabilityForCaisse(b, line.method) }}</a-select-option
            >
          </a-select>
          <p v-if="line.caisseBankId" class="text-muted small mb-0 mt-1">
            {{ $t("decaissement.availableForThisPayment") }}:
            <strong>{{ bankAvailabilityFormatted(line) }}</strong>
          </p>
          <p
            v-if="line.caisseBankId && lineBankAmountExceedsCaisse(line)"
            class="small mb-0 mt-1 text-danger"
          >
            {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
          </p>
        </div>

        <pay-cheque-section
          v-if="line.method === 'cheque'"
          :hide-bank-column="true"
          :rows="line.chequeRows"
          :total-required="Number(line.amount) || 0"
          @update="(i, f, v) => updateLineDetailRow(idx, 'chequeRows', i, f, v)"
          @add="addLineDetailRow(idx, 'chequeRows')"
          @remove="removeLineDetailRow(idx, 'chequeRows', $event)"
        />
        <pay-traite-section
          v-if="line.method === 'traite'"
          :hide-bank-column="true"
          :rows="line.traiteRows"
          :total-required="Number(line.amount) || 0"
          @update="(i, f, v) => updateLineDetailRow(idx, 'traiteRows', i, f, v)"
          @add="addLineDetailRow(idx, 'traiteRows')"
          @remove="removeLineDetailRow(idx, 'traiteRows', $event)"
        />
        <pay-virement-section
          v-if="line.method === 'virement'"
          :rows="line.virementRows"
          :total-required="Number(line.amount) || 0"
          @update="
            (i, f, v) => updateLineDetailRow(idx, 'virementRows', i, f, v)
          "
          @add="addLineDetailRow(idx, 'virementRows')"
          @remove="removeLineDetailRow(idx, 'virementRows', $event)"
        />
      </div>

      <a-button
        type="dashed"
        block
        icon="plus"
        class="mb-3"
        @click="addPaymentLine"
      >
        {{ $t("saisiePaiement.addPaymentLine") }}
      </a-button>

      <div
        class="sum-bar mb-3"
        :class="{ match: splitsSumOk, mismatch: !splitsSumOk }"
      >
        <span
          >{{ $t("saisiePaiement.splitsTotal") }}: {{ splitsSum.toFixed(3) }} /
          {{ selectedTotal.toFixed(3) }}</span
        >
        <a-tag :color="splitsSumOk ? 'green' : 'red'">{{
          splitsSumOk ? "OK" : $t("saisiePaiement.splitsSumMismatch")
        }}</a-tag>
      </div>

      <div>
        <label class="filter-label">{{
          $t("decaissement.receiptNumber")
        }}</label>
        <a-input v-model="receiptNumber" />
      </div>
    </a-modal>

    <a-drawer
      :visible="scheduleManageVisible"
      :title="$t('saisiePaiement.drawerDepenseSchedulesTitle')"
      placement="right"
      :width="drawerWidthScheduleEdit"
      :destroy-on-close="true"
      @close="closeScheduleManage"
    >
      <template v-if="scheduleManageDepense">
        <a-divider orientation="left">{{
          $t("saisiePaiement.depenseDetailsSection")
        }}</a-divider>
        <a-descriptions
          bordered
          :column="2"
          size="small"
          class="mb-3 depense-schedules-drawer-desc"
        >
          <a-descriptions-item :label="$t('bulletin.label')" :span="2">
            {{ scheduleManageDepense.label || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('comptabilite.reference')">
            {{ scheduleManageDepense.reference || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('saisiePaiement.invoiceRef')">
            {{ scheduleManageDepense.invoiceNumber || "—" }}
          </a-descriptions-item>
          <a-descriptions-item
            :label="$t('saisiePaiement.filterBeneficiaryType')"
          >
            {{ depenseBeneficiaryTypeLabel(scheduleManageDepense) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('saisiePaiement.beneficiary')">
            {{ depenseBeneficiaryName(scheduleManageDepense) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('saisiePaiement.expenseMode')">
            <a-tag
              v-if="scheduleManageDepense.expenseMode === 'normal'"
              color="blue"
              >{{ $t("decaissement.normal") }}</a-tag
            >
            <a-tag
              v-else-if="scheduleManageDepense.expenseMode === 'declaree'"
              color="orange"
              >{{ $t("decaissement.declaree") }}</a-tag
            >
            <span v-else>—</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.invoiceDate')">
            {{ formatDate(scheduleManageDepense.date) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.depenseCreatedAt')">
            {{ formatDate(scheduleManageDepense.entryDate || scheduleManageDepense.createdAt) }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="depenseCategoryLine(scheduleManageDepense)"
            :label="$t('comptabilite.category')"
            :span="2"
          >
            {{ depenseCategoryLine(scheduleManageDepense) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.totalToPay')">
            <span class="amount-cell">{{
              (scheduleManageDepense._totalAmount || 0).toFixed(3)
            }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.totalPaid')">
            <span class="amount-cell">{{
              (scheduleManageDepense.totalPaid || 0).toFixed(3)
            }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.invoiceStatus')">
            <a-tag
              :color="
                invoiceStatusTagColor(scheduleManageDepense.invoiceStatus)
              "
            >
              {{
                $t(
                  "fournisseur." +
                    (scheduleManageDepense.invoiceStatus || "unpaid")
                )
              }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.schedulingStatus')">
            <a-tag
              :color="
                scheduleManageDepense.schedulingStatus === 'scheduled'
                  ? 'blue'
                  : 'default'
              "
            >
              {{
                scheduleManageDepense.schedulingStatus === "scheduled"
                  ? $t("fournisseur.scheduled")
                  : $t("fournisseur.notScheduled")
              }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">{{ $t("dette.installments") }}</a-divider>
        <p class="text-muted small mb-3">
          {{ $t("saisiePaiement.schedulesEditHint") }}
        </p>
        <depense-schedules-panel
          :key="
            String(scheduleManageDepense._id) +
            '-' +
            String(scheduleClickedScheduleId || '')
          "
          :depense="scheduleManageDepense"
          :highlight-schedule-id="scheduleClickedScheduleId"
          :show-section-title="false"
          @refresh="onSchedulePanelRefresh"
        />
      </template>
    </a-drawer>
  </div>
</template>

<script>
import moment from "moment";
import {
  fetchPendingSchedules,
  executeGroupedPayment,
} from "@/services/api/compV3/paymentEntry";
import { fetchDecaissementLookups } from "@/services/api/compV3/decaissement";
import { showApiError } from "@/utilities/showApiError";
import {
  findCashCaisseInLookups,
  findBankCaisseInLookups,
  availableDecaissementForMethod,
  cashLineAmountExceedsAvailability,
  bankLineAmountExceedsAvailability,
  lineAmountExceedsPaymentSource,
} from "@/utilities/depense/caisseDecaissementAvailability";
import "../components/paymentCaisseInsufficient.css";
import BeneficiaryTypeDisplay from "../components/BeneficiaryTypeDisplay.vue";
import BeneficiaryTypesLegend from "../components/BeneficiaryTypesLegend.vue";
import PayChequeSection from "../components/paymentDetails/PayChequeSection.vue";
import PayTraiteSection from "../components/paymentDetails/PayTraiteSection.vue";
import PayVirementSection from "../components/paymentDetails/PayVirementSection.vue";
import DepenseSchedulesPanel from "../suppliers/components/DepenseSchedulesPanel.vue";

export default {
  name: "PaymentEntryPage",
  components: {
    BeneficiaryTypeDisplay,
    BeneficiaryTypesLegend,
    PayChequeSection,
    PayTraiteSection,
    PayVirementSection,
    DepenseSchedulesPanel,
  },
  data() {
    return {
      rows: [],
      loading: false,
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: {
        beneficiaryType: undefined,
        expenseMode: undefined,
        dateRange: [],
        search: "",
      },
      selectedIds: [],
      payModalVisible: false,
      paying: false,
      lookups: {
        myCaisses: [],
        otherCaisses: [],
        depenseCashCaisses: [],
        caisseBanks: [],
      },
      paymentLines: [],
      receiptNumber: "",
      scheduleManageVisible: false,
      scheduleManageDepense: null,
      scheduleClickedScheduleId: null,
    };
  },
  computed: {
    drawerWidthScheduleEdit() {
      return Math.min(
        960,
        typeof window !== "undefined" ? window.innerWidth - 48 : 960
      );
    },
    selectedTotal() {
      return this.rows
        .filter((s) => this.selectedIds.includes(s._id))
        .reduce((sum, s) => sum + s.amount, 0);
    },
    /** A user can own several caisses, all usable whatever the active school year. */
    myCaisses() {
      return this.lookups.myCaisses || [];
    },
    myCaisseId() {
      return this.myCaisses.length ? this.myCaisses[0]._id : undefined;
    },
    depenseCashCaisses() {
      return this.lookups.depenseCashCaisses || [];
    },
    depenseOtherCashOptions() {
      const mine = new Set(this.myCaisses.map((c) => String(c._id)));
      if (!mine.size) return this.depenseCashCaisses;
      return this.depenseCashCaisses.filter((c) => !mine.has(String(c._id)));
    },
    payModalWidth() {
      return "70%";
    },
    splitsSum() {
      return this.paymentLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    splitsSumOk() {
      return Math.abs(this.splitsSum - this.selectedTotal) < 0.01;
    },
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        showTotal: (t) => `${t}`,
      };
    },
    columns() {
      return [
        {
          title: this.$t("saisiePaiement.filterBeneficiaryType"),
          dataIndex: "_beneficiaryType",
          key: "benType",
          width: 140,
          scopedSlots: { customRender: "beneficiaryType" },
        },
        {
          title: this.$t("saisiePaiement.expenseLabel"),
          dataIndex: "_expenseLabel",
          key: "label",
          ellipsis: true,
          scopedSlots: { customRender: "expenseLabel" },
        },
        {
          title: this.$t("saisiePaiement.invoiceRef"),
          dataIndex: "_invoiceRef",
          key: "ref",
          scopedSlots: { customRender: "invoiceRef" },
        },
        {
          title: this.$t("saisiePaiement.beneficiary"),
          dataIndex: "_beneficiaryName",
          key: "ben",
          scopedSlots: { customRender: "beneficiary" },
        },
        {
          title: this.$t("saisiePaiement.expenseMode"),
          dataIndex: "_expenseMode",
          key: "mode",
          width: 110,
          scopedSlots: { customRender: "expenseMode" },
        },
        {
          title: this.$t("saisiePaiement.dueDate"),
          dataIndex: "dueDate",
          key: "due",
          width: 120,
          scopedSlots: { customRender: "dueDate" },
        },
        {
          title: this.$t("saisiePaiement.amount"),
          dataIndex: "amount",
          key: "amt",
          width: 120,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          scopedSlots: { customRender: "action" },
          width: 90,
        },
      ];
    },
  },
  async created() {
    await Promise.all([this.fetchData(), this.loadLookups()]);
  },
  methods: {
    cashAvailabilityForCaisse(caisse, method) {
      const v = availableDecaissementForMethod(caisse, method);
      return v == null ? "—" : v.toFixed(3);
    },
    cashAvailabilityFormatted(line) {
      const c = findCashCaisseInLookups(this.lookups, line.caisseId);
      const v = availableDecaissementForMethod(c, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineCashAmountExceedsCaisse(line) {
      return cashLineAmountExceedsAvailability(this.lookups, line);
    },
    bankAvailabilityForCaisse(bank, method) {
      const v = availableDecaissementForMethod(bank, method);
      return v == null ? "—" : v.toFixed(3);
    },
    bankAvailabilityFormatted(line) {
      const b = findBankCaisseInLookups(this.lookups, line.caisseBankId);
      const v = availableDecaissementForMethod(b, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineBankAmountExceedsCaisse(line) {
      return bankLineAmountExceedsAvailability(this.lookups, line);
    },
    lineAmountExceedsAvailable(line) {
      return lineAmountExceedsPaymentSource(this.lookups, line);
    },
    formatDate(v) {
      return v ? moment(v).format("DD/MM/YYYY") : "—";
    },
    formatDateTime(v) {
      return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—";
    },
    buildQueryParams() {
      const p = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
      };
      if (this.filters.beneficiaryType)
        p.beneficiaryType = this.filters.beneficiaryType;
      if (this.filters.expenseMode) p.expenseMode = this.filters.expenseMode;
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        p.dueDateFrom = this.filters.dateRange[0].format("YYYY-MM-DD");
        p.dueDateTo = this.filters.dateRange[1].format("YYYY-MM-DD");
      }
      const searchTerm =
        (this.filters.search && String(this.filters.search).trim()) || "";
      if (searchTerm) p.search = searchTerm;
      return p;
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await fetchPendingSchedules(this.buildQueryParams());
        const body = res.data || {};
        this.rows = body.data || [];
        this.pagination.total = body.total || 0;
        this.pagination.current = body.page || 1;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    async loadLookups() {
      try {
        const res = await fetchDecaissementLookups();
        const ps = res.data?.paymentSources || {};
        this.lookups = {
          myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
          otherCaisses: ps.otherCaisses || [],
          depenseCashCaisses: ps.depenseCashCaisses || [],
          caisseBanks: ps.caisseBanks || [],
        };
      } catch {
        /* silent */
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.selectedIds = [];
      this.fetchData();
    },
    resetFilters() {
      this.filters = {
        beneficiaryType: undefined,
        expenseMode: undefined,
        dateRange: [],
        search: "",
      };
      this.applyFilters();
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.fetchData();
    },
    onSelectChange(keys) {
      this.selectedIds = keys;
    },
    rowClickHandler(record) {
      return {
        on: {
          click: () => {
            const idx = this.selectedIds.indexOf(record._id);
            if (idx >= 0) {
              this.selectedIds = this.selectedIds.filter(
                (k) => k !== record._id
              );
            } else {
              this.selectedIds = [...this.selectedIds, record._id];
            }
          },
        },
      };
    },
    rowClassName(record, index) {
      const selected = this.selectedIds.includes(record._id)
        ? "row-selected"
        : "";
      const stripe = index % 2 === 0 ? "table-row-even" : "table-row-odd";
      const debt = record._isDebtInstallment ? "row-debt-installment" : "";
      return `${stripe} ${selected} ${debt}`.trim();
    },
    emptyChequeRow() {
      return {
        num: null,
        dateEchance: null,
        bank: undefined,
        titulaire: null,
        montant: 0,
      };
    },
    emptyTraiteRow() {
      return {
        num: null,
        dateEchance: null,
        bank: undefined,
        rib: null,
        montant: 0,
      };
    },
    emptyVirementRow() {
      return { num: null, titulaire: null, montant: 0 };
    },
    defaultCashFundFields() {
      if (this.myCaisseId) {
        return { fundDestination: "myCaisse", caisseId: this.myCaisseId };
      }
      const first = this.depenseOtherCashOptions[0];
      if (first) return { fundDestination: "otherCaisse", caisseId: first._id };
      return { fundDestination: "myCaisse", caisseId: undefined };
    },
    createPaymentLine() {
      const cash = this.defaultCashFundFields();
      return {
        uid: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        method: "cash",
        amount: null,
        fundDestination: cash.fundDestination,
        caisseId: cash.caisseId,
        caisseBankId: undefined,
        chequeRows: [this.emptyChequeRow()],
        traiteRows: [this.emptyTraiteRow()],
        virementRows: [this.emptyVirementRow()],
      };
    },
    onLineFundDestinationChange(idx) {
      const line = { ...this.paymentLines[idx] };
      if (line.fundDestination === "myCaisse") {
        line.caisseId = this.myCaisseId;
      } else {
        const first = this.depenseOtherCashOptions[0];
        line.caisseId = first ? first._id : undefined;
      }
      this.$set(this.paymentLines, idx, line);
    },
    onLineMethodChange(idx) {
      const line = { ...this.paymentLines[idx] };
      line.chequeRows = [this.emptyChequeRow()];
      line.traiteRows = [this.emptyTraiteRow()];
      line.virementRows = [this.emptyVirementRow()];
      if (line.method === "cash") {
        const cash = this.defaultCashFundFields();
        line.fundDestination = cash.fundDestination;
        line.caisseId = cash.caisseId;
        line.caisseBankId = undefined;
      } else {
        line.caisseId = undefined;
        line.fundDestination = undefined;
        if (
          !line.caisseBankId &&
          this.lookups.caisseBanks &&
          this.lookups.caisseBanks.length
        ) {
          line.caisseBankId = this.lookups.caisseBanks[0]._id;
        }
      }
      this.$set(this.paymentLines, idx, line);
    },
    updateLineDetailRow(lineIdx, arrKey, rowIdx, field, val) {
      const line = { ...this.paymentLines[lineIdx] };
      const arr = [...line[arrKey]];
      arr[rowIdx] = { ...arr[rowIdx], [field]: val };
      line[arrKey] = arr;
      this.$set(this.paymentLines, lineIdx, line);
    },
    addLineDetailRow(lineIdx, arrKey) {
      const templates = {
        chequeRows: this.emptyChequeRow(),
        traiteRows: this.emptyTraiteRow(),
        virementRows: this.emptyVirementRow(),
      };
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = [...line[arrKey], { ...templates[arrKey] }];
      this.$set(this.paymentLines, lineIdx, line);
    },
    removeLineDetailRow(lineIdx, arrKey, rowIdx) {
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = line[arrKey].filter((_, i) => i !== rowIdx);
      this.$set(this.paymentLines, lineIdx, line);
    },
    detailSumForLine(line) {
      if (line.method === "cheque")
        return (line.chequeRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      if (line.method === "traite")
        return (line.traiteRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      if (line.method === "virement")
        return (line.virementRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      return 0;
    },
    formatMethodDetailsRow(r) {
      return {
        ...r,
        dateEchance:
          r.dateEchance && r.dateEchance.format
            ? r.dateEchance.format("YYYY-MM-DD")
            : r.dateEchance || null,
      };
    },
    buildLineMethodDetails(line) {
      if (line.method === "cheque") {
        return {
          cheques: line.chequeRows.map((r) => this.formatMethodDetailsRow(r)),
        };
      }
      if (line.method === "traite") {
        return {
          traites: line.traiteRows.map((r) => this.formatMethodDetailsRow(r)),
        };
      }
      if (line.method === "virement") {
        return { virements: line.virementRows.map((r) => ({ ...r })) };
      }
      return {};
    },
    addPaymentLine() {
      const line = this.createPaymentLine();
      line.amount = 0;
      line.method = "cash";
      const cash = this.defaultCashFundFields();
      line.fundDestination = cash.fundDestination;
      line.caisseId = cash.caisseId;
      this.paymentLines = [...this.paymentLines, line];
    },
    removePaymentLine(idx) {
      if (this.paymentLines.length <= 1) return;
      this.paymentLines = this.paymentLines.filter((_, i) => i !== idx);
    },
    openPayModal() {
      if (!this.selectedIds.length) {
        this.$message.warning(this.$t("saisiePaiement.selectAtLeastOne"));
        return;
      }
      this.receiptNumber = "";
      const line = this.createPaymentLine();
      line.amount = this.selectedTotal;
      this.paymentLines = [line];
      this.payModalVisible = true;
    },
    submitPayment(e) {
      e.preventDefault();
      if (!this.splitsSumOk) {
        this.$message.error(this.$t("saisiePaiement.splitsSumMismatch"));
        return;
      }
      for (let i = 0; i < this.paymentLines.length; i += 1) {
        const line = this.paymentLines[i];
        const amt = Number(line.amount);
        if (!amt || amt <= 0) {
          this.$message.error(this.$t("saisiePaiement.lineAmountRequired"));
          return;
        }
        if (line.method === "cash") {
          if (!line.caisseId) {
            this.$message.error(this.$t("requis.required"));
            return;
          }
        } else if (!line.caisseBankId) {
          this.$message.error(this.$t("requis.required"));
          return;
        }
        if (["cheque", "traite", "virement"].includes(line.method)) {
          const ds = this.detailSumForLine(line);
          if (Math.abs(ds - amt) > 0.01) {
            this.$message.error(this.$t("decaissement.methodDetailsSumError"));
            return;
          }
        }
      }

      const splits = this.paymentLines.map((line) => {
        const o = {
          method: line.method,
          amount: Number(line.amount),
          caisseId: line.method === "cash" ? line.caisseId : undefined,
          caisseBankId: line.method !== "cash" ? line.caisseBankId : undefined,
        };
        if (["cheque", "traite", "virement"].includes(line.method)) {
          o.methodDetails = this.buildLineMethodDetails(line);
        } else {
          o.methodDetails = {};
        }
        return o;
      });

      this.paying = true;
      executeGroupedPayment({
        scheduleIds: this.selectedIds,
        splits,
        receiptNumber: this.receiptNumber || "",
      })
        .then(async () => {
          this.$message.success(this.$t("saisiePaiement.paymentExecuted"));
          this.payModalVisible = false;
          this.selectedIds = [];
          await this.fetchData();
        })
        .catch((ex) => {
          showApiError(this, ex);
        })
        .finally(() => {
          this.paying = false;
        });
    },
    buildDepenseForSchedulePanel(record) {
      const raw = record && record._depense;
      if (!raw || !raw._id) return null;
      const total = Number(raw.amountTTC ?? raw.amount ?? raw.money ?? 0);
      return {
        ...raw,
        _totalAmount: total,
      };
    },
    depenseBeneficiaryName(dep) {
      if (!dep) return "—";
      if (
        dep.beneficiaryType === "debiteur" &&
        dep.debiteur &&
        typeof dep.debiteur === "object"
      ) {
        return dep.debiteur.name || dep.debiteur.reference || "—";
      }
      if (dep.provider && typeof dep.provider === "object")
        return dep.provider.fullName || "—";
      if (dep.associate && typeof dep.associate === "object") {
        return dep.associate.fullName || dep.associate.name || "—";
      }
      if (dep.employee && typeof dep.employee === "object") {
        return (
          `${dep.employee.firstName || ""} ${
            dep.employee.lastName || ""
          }`.trim() || "—"
        );
      }
      if (dep.company != null) {
        if (typeof dep.company === "object" && dep.company.name)
          return dep.company.name;
        if (typeof dep.company === "string") return dep.company;
      }
      return "—";
    },
    depenseBeneficiaryTypeLabel(dep) {
      const bt = dep && dep.beneficiaryType;
      if (bt === "provider") return this.$t("factures.beneficiaryProvider");
      if (bt === "associate") return this.$t("factures.beneficiaryAssociate");
      if (bt === "employee") return this.$t("factures.beneficiaryEmployee");
      if (bt === "company") return this.$t("factures.beneficiaryCompany");
      if (bt === "debiteur") return this.$t("factures.beneficiaryDebiteur");
      return "—";
    },
    depenseCategoryLine(dep) {
      if (!dep) return "";
      const cat =
        dep.category && typeof dep.category === "object"
          ? dep.category.name
          : "";
      const sub =
        dep.subCategory && typeof dep.subCategory === "object"
          ? dep.subCategory.name
          : "";
      if (cat && sub) return `${cat} / ${sub}`;
      return cat || sub || "";
    },
    invoiceStatusTagColor(s) {
      if (s === "paid") return "green";
      if (s === "partial") return "orange";
      return "red";
    },
    editSchedule(record) {
      const dep = this.buildDepenseForSchedulePanel(record);
      if (!dep) {
        this.$message.error(
          this.$t("saisiePaiement.cannotLoadDepenseForSchedules")
        );
        return;
      }
      this.scheduleClickedScheduleId = record._id;
      this.scheduleManageDepense = dep;
      this.scheduleManageVisible = true;
    },
    closeScheduleManage() {
      this.scheduleManageVisible = false;
      this.scheduleManageDepense = null;
      this.scheduleClickedScheduleId = null;
    },
    onSchedulePanelRefresh() {
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.ben-type-filter-select >>> .ant-select-selection-selected-value {
  display: flex;
  align-items: center;
}
.ben-type-filter-select >>> .ben-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pay-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 12px 16px;
}

.row-selected td {
  background: #e6f7ff !important;
}

.payment-split-card {
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
}
.sum-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  font-weight: 600;
}
.sum-bar.match {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.sum-bar.mismatch {
  background: #fff2f0;
  border: 1px solid #ffccc7;
}
</style>

<style>
@import "~@/views/compV3/common.css";
.row-debt-installment td {
  background: #f9f0ff !important;
}
</style>
